<template>
  <EquipmentRow :key="`${intake?.id}-${index}`" data-cy="equipment-row">
    <template #left> </template>
    <template #default="{ toggle }">
      <div class="flex flex-column gap-2 intake-row-content">
        <AddRemoveButtons
          :disable-add-above="disableAddAbove"
          :disable-add-below="disableAddBelow"
          :disable-remove="disableRemove"
          :hide-add-above="hideAddAbove"
          :hide-add-below="hideAddBelow"
          :hide-remove="hideRemove"
          data-cy="add-remove-buttons"
          class="intake-add-remove-buttons"
          @add-above="$emit('add-above')"
          @add-below="$emit('add-below')"
          @remove="$emit('remove', intake)"
        >
          <template #middle-content>
            <EquipmentPanel
              class="flex-grow-1 w-full intake-panel"
              :rule-names="[
                'intake_size_top_casing',
                'intake_size_bottom_casing',
                'intake_shaft_limit_std',
                'intake_obsolete',
                'gas_separation_max_flow_exceeded',
                'intake_use_natural_gas_separation'
              ]"
              :xref-id="intake?.id"
              data-cy="equipment-panel"
              @toggle="toggle"
            >
              <template #header>Suction Type</template>
              <template v-if="intake?.r_intake" #trigger>
                {{
                  computeIntakeLabel(
                    intake?.r_intake,
                    intake?.use_high_strength_shaft
                  )
                }}
              </template>
            </EquipmentPanel>
          </template>
        </AddRemoveButtons>
        <div class="flex align-items-center gap-2" data-cy="shaft-row">
          <div class="field-checkbox align-items-center flex">
            <AlertWrapper :rule-names="['intake_shaft_limit_std']">
              <Checkbox
                :model-value="intake?.use_high_strength_shaft || false"
                :disabled="!intake?.r_intake || isScenarioReadonly"
                :input-id="`high-strength-shaft-${intake?.id}`"
                binary
                data-cy="checkbox-high-strength-shaft"
                @update:model-value="
                  $emit('update:intakes', {
                    ...intake,
                    use_high_strength_shaft: $event
                  })
                "
              />
              <label
                :for="`high-strength-shaft-${intake?.id}`"
                :class="{ disabled: !intake?.r_intake }"
              >
                Use High Strength Shaft
              </label>
            </AlertWrapper>
          </div>
        </div>
        <div
          v-if="intake?.r_intake && showIntakeStats"
          class="flex align-items-center gap-2 flex-wrap"
          data-cy="intake-diameter-row"
        >
          <div class="mr-2 flex-1" style="min-width: 150px">
            <ConverterInput
              label="Primary OD"
              :model-value="intake?.r_intake?.outer_diameter_inches"
              :base-unit="LengthUnits.Inches"
              :target-unit="getLargeLengthUnit()"
              :model="lengthUnitModel"
              mode="convert"
              disabled
              data-cy="converter-input-primary-od"
            />
          </div>
          <div class="mr-2 flex-1" style="min-width: 150px">
            <ConverterInput
              label="Inner Diameter"
              :model-value="intake?.r_intake?.inner_diameter_inches"
              :base-unit="LengthUnits.Inches"
              :target-unit="getLargeLengthUnit()"
              :model="lengthUnitModel"
              mode="convert"
              disabled
              data-cy="converter-input-inner-diameter"
            />
          </div>
        </div>
        <div
          v-if="showIntakeStats"
          class="flex align-items-center gap-2 flex-wrap"
          data-cy="intake-details-row"
        >
          <div class="mr-2 flex-1" style="min-width: 150px">
            <ConverterInput
              label="Length"
              :model-value="intake?.r_intake?.length_inches"
              :base-unit="LengthUnits.Inches"
              :target-unit="getLargeLengthUnit()"
              :model="lengthUnitModel"
              mode="convert"
              data-cy="converter-input"
            />
          </div>
          <div class="mr-2 flex-1" style="min-width: 150px">
            <ConverterInput
              label="Weight"
              :model-value="intake?.r_intake?.weight_pounds"
              :base-unit="MassUnits.Pounds"
              :target-unit="getMassUnit()"
              :model="massUnitModel"
              mode="convert"
            />
          </div>
        </div>
      </div>
    </template>
    <template #expand="{ toggle }">
      <HpsIntakeSearchView
        :is-admin="false"
        data-cy="intake-search-view"
        @select="handleHPSIntakeSelect(intake, $event, toggle)"
      />
    </template>
    <template #right></template>
  </EquipmentRow>
</template>

<script setup lang="ts">
import type { PropType } from "vue";
import { provide, computed } from "vue";
import { LengthUnits, LargeLengthUnits } from "@/libs/units/length";
import { MassUnits } from "@/libs/units/mass";
import { computeIntakeLabel } from "@/models/equipment/intake/properties";
import EquipmentRow from "@/components/scenarios/EquipmentRow.vue";
import AddRemoveButtons from "@/components/AddRemoveButtons.vue";
import EquipmentPanel from "@/components/scenarios/EquipmentPanel.vue";
import ConverterInput from "@/components/ConverterInput.vue";
import HpsIntakeSearchView from "@/components/equipment/hps_intakes/HpsIntakeSearchView.vue";
import AlertWrapper from "@/components/AlertWrapper.vue";
import type { XrefIntakes } from "@/composables/forms/formTypes";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";
import {
  scenarioFormKey,
  scenarioKey,
  sizingKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { injectStrict } from "@/utils/utils";
import { useEditPermissions } from "@/composables/auth/editPermissions";
import type { Intakes } from "gql/api";

const scenarioForm = injectStrict(scenarioFormKey, null);
const { isScenarioReadonly } = useEditPermissions(undefined, scenarioForm);
const scenario = injectStrict(scenarioKey);
const sizing = injectStrict(sizingKey, null);
const { getLargeLengthUnit, getMassUnit } = useScenarioUnits(scenario, sizing);

const lengthUnitModel = LargeLengthUnits.options();
const massUnitModel = MassUnits.options();
const showIntakeStats = computed(() => false);

const props = defineProps({
  intake: {
    type: Object as PropType<XrefIntakes[number]>,
    default: null
  },
  index: {
    type: Number,
    default: undefined
  },
  hideAddAbove: {
    type: Boolean,
    default: false
  },
  hideAddBelow: {
    type: Boolean,
    default: false
  },
  hideRemove: {
    type: Boolean,
    default: false
  },
  disableAddAbove: {
    type: Boolean,
    default: false
  },
  disableAddBelow: {
    type: Boolean,
    default: false
  },
  disableRemove: {
    type: Boolean,
    default: false
  }
});

const $emit = defineEmits<{
  (e: "add-below"): void;
  (e: "add-above"): void;
  (e: "remove", intake: XrefIntakes[number]): void;
  (e: "update:intakes", intake: XrefIntakes[number]): void;
}>();

provide(
  "selected-equipment-id",
  computed(() => props.intake?.r_intake?.id)
);

function handleHPSIntakeSelect(
  xrefIntake: XrefIntakes[number],
  selectedHPSIntakeEquipment: Intakes,
  toggle: () => void
) {
  toggle();
  $emit("update:intakes", {
    ...xrefIntake,
    id: xrefIntake?.id,
    intake_id: selectedHPSIntakeEquipment.id,
    r_intake: selectedHPSIntakeEquipment,
    use_high_strength_shaft: xrefIntake.use_high_strength_shaft ?? true
  });
}
</script>

<style lang="scss" scoped>
.intake-row-content {
  width: 100%;
}

.intake-add-remove-buttons {
  width: 100%;

  :deep(.add-remove-button-container) {
    justify-content: flex-start;
    width: 100%;
  }

  :deep(.middle-content) {
    flex: 1 1 auto;
    min-width: 0;
  }
}

.intake-panel {
  :deep(.p-panel) {
    width: 100%;
  }

  :deep(.p-panel-content) {
    width: 100%;
  }
}

[data-cy="intake-diameter-row"],
[data-cy="intake-details-row"] {
  width: 100%;

  .mr-2 {
    :deep(.converter) {
      width: 100%;
    }
  }
}
</style>
